import React from 'react';
import { Container, Row, Col, Card, Button, Badge, Breadcrumb } from 'react-bootstrap';
import SEOHead from '../components/SEOHead';
import { businessData } from '../data/businessData';
import { Service } from '../types';

interface ServicesPageProps {
  onServiceSelect: (service: Service) => void;
}

const ServicesPage: React.FC<ServicesPageProps> = ({ onServiceSelect }) => {
  const getServiceIcon = (serviceName: string) => {
    if (serviceName.includes('Wash')) return '🚿';
    if (serviceName.includes('Denting')) return '🔧';
    if (serviceName.includes('Battery')) return '🔋';
    if (serviceName.includes('Mechanical')) return '⚙️';
    if (serviceName.includes('AC')) return '❄️';
    if (serviceName.includes('Clutch')) return '🔩';
    if (serviceName.includes('Lights')) return '💡';
    if (serviceName.includes('Regular')) return '🛠️';
    if (serviceName.includes('Suspension')) return '🚗';
    if (serviceName.includes('Tyre')) return '⚪';
    if (serviceName.includes('Polishing')) return '✨';
    if (serviceName.includes('Inspections')) return '🔍';
    return '🔧';
  };

  return (
    <>
      <SEOHead page="services" />
      
      {/* Breadcrumb */}
      <Container className="py-3">
        <Breadcrumb>
          <Breadcrumb.Item href="#home">Home</Breadcrumb.Item>
          <Breadcrumb.Item active>Services</Breadcrumb.Item>
        </Breadcrumb>
      </Container>

      {/* Services Header */}
      <section className="py-5 bg-primary text-white">
        <Container>
          <div className="text-center">
            <h1 className="display-4 fw-bold mb-3">Our Professional Car Services</h1>
            <p className="lead mb-4">
              Complete automobile repair and maintenance solutions in Indore since 2011
            </p>
            <Badge bg="warning" text="dark" className="fs-6 px-3 py-2">
              Starting from ₹250 | All Car Brands Supported
            </Badge>
          </div>
        </Container>
      </section>

      {/* Services Grid */}
      <section className="py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h2 className="h3 fw-bold text-dark mb-3">Complete Car Care Services</h2>
            <p className="text-muted mb-4">
              Professional quality services with transparent pricing and expert technicians
            </p>
            <Badge bg="info" className="mb-4">
              {businessData.price_note}
            </Badge>
          </div>

          <Row>
            {businessData.services.map((service, index) => (
              <Col lg={4} md={6} className="mb-4" key={service.id}>
                <Card className="h-100 shadow-sm border-0 service-card">
                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex align-items-center mb-3">
                      <div className="display-6 me-3">
                        {getServiceIcon(service.service_name)}
                      </div>
                      <div>
                        <h3 className="h5 card-title mb-1">{service.service_name}</h3>
                        <div className="text-success fw-bold">
                          ₹{service.starting_price}+ {service.unit}
                        </div>
                      </div>
                    </div>
                    
                    <p className="card-text text-muted flex-grow-1 mb-3">
                      {service.description}
                    </p>
                    
                    <div className="mt-auto">
                      <Button 
                        variant="primary" 
                        className="w-100 fw-semibold"
                        onClick={() => onServiceSelect(service)}
                      >
                        Book This Service
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Service Categories */}
          <Row className="mt-5">
            <Col lg={12}>
              <h3 className="text-center fw-bold mb-4">Service Categories</h3>
              <Row>
                <Col md={3} className="mb-3">
                  <Card className="text-center border-primary">
                    <Card.Body>
                      <div className="display-6 text-primary mb-2">🚿</div>
                      <h5 className="fw-bold">Cleaning Services</h5>
                      <p className="small text-muted">Car wash, polishing, detailing</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} className="mb-3">
                  <Card className="text-center border-success">
                    <Card.Body>
                      <div className="display-6 text-success mb-2">🔧</div>
                      <h5 className="fw-bold">Body Work</h5>
                      <p className="small text-muted">Denting, painting, body repairs</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} className="mb-3">
                  <Card className="text-center border-warning">
                    <Card.Body>
                      <div className="display-6 text-warning mb-2">⚙️</div>
                      <h5 className="fw-bold">Mechanical</h5>
                      <p className="small text-muted">Engine, transmission, brakes</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} className="mb-3">
                  <Card className="text-center border-info">
                    <Card.Body>
                      <div className="display-6 text-info mb-2">🔍</div>
                      <h5 className="fw-bold">Maintenance</h5>
                      <p className="small text-muted">Regular service, inspections</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Why Choose Our Services */}
          <Row className="mt-5">
            <Col lg={12}>
              <Card className="bg-dark text-white">
                <Card.Body className="py-5">
                  <h3 className="text-center fw-bold mb-4">Why Choose Our Services?</h3>
                  <Row>
                    <Col md={4} className="text-center mb-3">
                      <div className="display-6 mb-3">✅</div>
                      <h5>Quality Guarantee</h5>
                      <p>100% satisfaction guaranteed on all services</p>
                    </Col>
                    <Col md={4} className="text-center mb-3">
                      <div className="display-6 mb-3">⚡</div>
                      <h5>Quick Service</h5>
                      <p>Most services completed same day</p>
                    </Col>
                    <Col md={4} className="text-center mb-3">
                      <div className="display-6 mb-3">💰</div>
                      <h5>Transparent Pricing</h5>
                      <p>No hidden charges, upfront estimates</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <style>{`
        .service-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .service-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important;
        }
      `}</style>
    </>
  );
};

export default ServicesPage;